// U-S5A-1 — главная для empty-state (0 загруженных анализов).
// Renamed from SurveyDone.tsx (Stage 1 §9.4 H1 fix).
//
// Стандартная оболочка main-screen: MobileFrame + ScreenHeader "Главная" + BottomNav.
// Различия с HomeConfirmed — только body (hero CTA + AI блок + "Анкета заполнена" +
// amber disclaimer вместо dashboard'a с биомаркерами).
//
// Логаут / История загрузок живут в Профиле (BottomNav→Профиль), не на главной.

import { useState } from 'react';
import { useLocation } from 'wouter';
import { ChevronRight, FileText, Sparkles, UploadCloud } from 'lucide-react';
import { MobileFrame } from '@/components/layout/MobileFrame';
import { IconTile } from '@/components/ui/icon-tile';
import { ScreenHeader } from '@/components/layout/ScreenHeader';
import { NotificationsBell } from '@/components/notifications/NotificationsBell';
import { BottomNav } from '@/components/layout/BottomNav';
import { BottomSheet } from '@/components/ui/bottom-sheet';
import { WellnessInsightsCard } from '@/components/wellness/WellnessInsightsCard';
import { UploadSourcePicker } from '@/components/uploads/UploadSourcePicker';
import { PendingReviewBarContainer } from '@/components/uploads/PendingReviewBarContainer';
import { useAuthMe } from '@/api/hooks/auth';
import { greetingText } from '@/components/dashboard/HomeGreeting';
import { setGuideOrigin } from '@/lib/guideNav';

export function HomeEmpty() {
  const [, setLocation] = useLocation();
  const [isSheetOpen, setIsSheetOpen] = useState(false);
  // Тот же источник и то же поле, что у приветствия на подтверждённой Главной
  // (HomeGreeting): first_name после name-split. Иначе на одном экране было бы
  // «Здравствуйте, Саят», а на другом — с фамилией. Пока /auth/me в полёте,
  // greetingText отдаёт нейтральное приветствие и экран не блокируется.
  const greeting = greetingText(useAuthMe().data?.user.first_name);

  return (
    <MobileFrame>
      <ScreenHeader title="Главная" right={<NotificationsBell />} />

      <main className="flex flex-1 flex-col overflow-y-auto px-5 pb-8 pt-2">
        {/* HEAL-46a D-18 — у человека без подтверждённых анализов Главная пустая,
            но непроверенные у него уже могут быть: он вышел с экрана проверки.

            Строка стоит ПОД шапкой и внутри скролла — там же, где на Главной с
            данными. До этого она была первым ребёнком MobileFrame, то есть выше
            заголовка экрана: читалась как чужой элемент над приложением и не
            уезжала при прокрутке, хотя на второй Главной уезжает. Один элемент
            на двух Главных жил в двух разных местах.

            Горизонтальный отступ здесь НЕ нужен: его уже даёт px-5 у <main>.
            empty:hidden — контейнер отдаёт null, когда проверять нечего, и без
            этого обёртка оставляла бы пустой зазор над приветствием у всех,
            у кого непроверенных нет, то есть почти у всех. */}
        <div className="mb-4 empty:hidden">
          <PendingReviewBarContainer />
        </div>

        {/* §2: заголовок «Загрузите первый анализ» дублировал кнопку ниже —
            один и тот же призыв дважды на одном экране. */}
        <section>
          <h1 className="mb-1 text-hl-title font-bold leading-tight">
            {greeting}
          </h1>
          <p className="mb-6 text-sm text-muted-foreground">
            Пока здесь пусто. Загрузите первый анализ — и появятся показатели, графики и динамика.
          </p>
        </section>

        {/* Блок загрузки поднят СРАЗУ под подзаголовок, выше подсказок. Он был
            третьим — то есть между входом и единственным нужным действием
            стоял текст. Загрузка первого анализа это наша единственная
            активационная веха. */}
        <section className="mb-6 rounded-surface bg-primary p-5 text-primary-foreground">
          <div className="mb-4 flex items-start gap-3">
            <IconTile size="md" tone="inverse">
              <UploadCloud className="h-6 w-6" aria-hidden="true" />
            </IconTile>
            <div>
              <div className="mb-1 text-lg font-semibold leading-tight">
                Загрузите первый анализ
              </div>
              <div className="text-sm leading-snug text-white/90">
                PDF из почты, файлов или фото — распознаём за 15–30 секунд
              </div>
            </div>
          </div>
          <button
            type="button"
            onClick={() => setIsSheetOpen(true)}
            className="block w-full rounded-surface bg-surface py-3 text-sm font-semibold text-primary-text"
          >
            Загрузить анализ
          </button>
        </section>

        {/* Уровень 3: вход в гайд для тех, кто придёт за ним сам. Карусели
            между регистрацией и первой загрузкой нет намеренно (D-9). */}
        <section className="mb-6">
          <button
            type="button"
            onClick={() => {
              setGuideOrigin('/');
              setLocation('/guide');
            }}
            className="flex w-full items-center gap-3 rounded-surface border border-border bg-surface p-4 text-left transition hover:bg-muted"
          >
            <IconTile size="sm">
              <Sparkles className="h-5 w-5" aria-hidden="true" />
            </IconTile>
            <div className="min-w-0 flex-1">
              <div className="text-sm font-semibold">Как это работает</div>
              <div className="text-hl-small text-muted-foreground">Короткий гайд, 1 минута</div>
            </div>
            <ChevronRight className="h-4 w-4 flex-shrink-0 text-muted-foreground" aria-hidden="true" />
          </button>
        </section>

        <section className="mb-6">
          <WellnessInsightsCard />
        </section>

        <section className="mb-6">
          <div className="flex items-center gap-3 rounded-surface border border-border bg-surface p-4">
            <IconTile size="sm">
              <FileText className="h-5 w-5" aria-hidden="true" />
            </IconTile>
            <div className="min-w-0 flex-1">
              <div className="text-sm font-semibold">Анкета здоровья</div>
              <div className="text-xs text-muted-foreground">5 шагов · 2–3 минуты</div>
            </div>
            <div className="text-xs font-semibold text-primary-text">Заполнено</div>
          </div>
        </section>

        <div className="rounded-surface border border-amber-200 bg-amber-50 p-3 text-hl-small leading-snug text-amber-900">
          <strong>HealthLab не заменяет консультацию врача.</strong> При отклонениях — обратитесь к специалисту.
        </div>
      </main>

      <BottomNav active="home" />

      <BottomSheet
        open={isSheetOpen}
        onClose={() => setIsSheetOpen(false)}
        ariaLabel="Выбор источника файла"
      >
        <UploadSourcePicker
          onClose={() => setIsSheetOpen(false)}
          onUploaded={(uploadId) => setLocation(`/upload/processing/${uploadId}`)}
          onBatchCreated={(batchId) => setLocation(`/uploads/batch/${batchId}`)}
        />
      </BottomSheet>
    </MobileFrame>
  );
}
